import { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { GuestTestimonyCard } from './GuestTestimonyCard';

// Imágenes
import adrianaCastro from '../assets/adriana_castro.png';
import carlosRebollon from '../assets/carlos_rebollon.jpg';
import johanaBaquero from '../assets/johana_baquero.jpeg';
import rodrigoSalas from '../assets/rodrigo_salas_guzman.jpeg';
import carlosSalinas from '../assets/carlos_salinas.jpeg';
import massielRivera from '../assets/massiel_rivera.jpeg';

export const GuestTestimonyCarousel = ({
    autoPlayInterval = 5000,
    showDots = true,
    pauseOnHover = true,
    fadeDuration = 500,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);

    const testimonies = [
        {
            name: 'Adriana Castro',
            location: 'Costa Rica',
            subtitle: 'Alumna Workshop Silicon Valley',
            role: 'CEO & Founder Craving English',
            testimony:
                'Entré con la expectativa de aprender sobre innovación, tecnología y negocios. Y claro que lo hice. Pero lo que más me marcó, fue otra cosa. La calidad humana de quienes más han logrado. Personas humildees, generosas, apasionadas y empáticas. Líderes que no hablaban desde el ego, sino desde la experiencia. Que compartían sus fracasos con tanta naturalidad como sus éxitos. Ahí entendí que el liderazgo real no se mide por la cantidad de logros, sino por la capacidad de seguir siendo buenas personas en medio del éxito. Porque en un mundo donde todos quieren ser "el número uno", encontré gigantes que eligen seguir compartiendo todo lo que saben.',
            avatarUrl: adrianaCastro,
        },
        {
            name: 'Carlos Rebollón',
            location: 'Panamá',
            subtitle: 'Alumno Workshop Silicon Valley',
            role: 'Co-Founder Examedica Orthopedics Surgeon',
            testimony:
                'Participar del workshop fue una experiencia transformadora. Tuve la oportunidad de presentar mi pitch de Examedica en Plug and Play, un ecosistema donde nacen y crecen grandes empresas, además de conectar con líderes de distintas industrias. Creo que toda persona con espíritu emprendedor y especialmente quienes ocupan cargos gerenciales (C-Level) deberían vivir una experiencia así. La energía del lugar, sumada a las historias de éxito, inspiran profundamente a seguir creciendo.',
            avatarUrl: carlosRebollon,
        },
        {
            name: 'Johana Baquero',
            location: 'Colombia',
            subtitle: 'Alumna Workshop Silicon Valley',
            role: 'Sales Manager Siemens Healthineers',
            testimony:
                'Los distintos escenarios que nos brindaron despertaron momentos de inspiración y reflexión que sin duda nos impulsarán a dar los siguientes pasos en nuestros entornos corporativos, emprendimientos y proyectos personales. Me voy profundamente motivada a continuar este camino de formación y transformación, con la ilusión de que podamos seguir promoviendo cambios desde nuestras propias manos.',
            avatarUrl: johanaBaquero,
        },
        {
            name: 'Rodrigo Salas Guzmán',
            location: 'Costa Rica',
            subtitle: 'Alumno Workshop Silicon Valley',
            role: 'Business Development Consultant',
            testimony:
                'Cada conversación, cada experiencia compartida y cada sonrisa, dejaron en mí una huella profunda. Esta suma de momentos me permite crecer tanto personal como profesionalmente, y me inspira a seguir aprendiendo junto a personas tan valiosas como ustedes.',
            avatarUrl: rodrigoSalas,
        },
        {
            name: 'Carlos Salinas',
            location: 'Honduras',
            subtitle: 'Alumno Workshop Silicon Valley',
            role: 'VP Customer Experience Grupo Ficosha',
            testimony:
                'Lo vivido fue mucho más que un programa académico: fue una inmersión de liderazgo, innovación y propósito, donde cada detalle reflejó la excelencia, la visión y la pasión que caracterizan a ADEN. Desde las conferencias magistrales hasta las visitas empresariales, todo estuvo impregnado de una energía única, de ese sello humano y profesional que solo una institución con alma puede transmitir. Regreso con la mente llena de conocimiento, el corazón lleno de gratitud y el alma encendida con nuevas ideas y sueños.',
            avatarUrl: carlosSalinas,
        },
        {
            name: 'Massiel Rivera',
            location: 'República Dominicana',
            subtitle: 'Alumna Workshop Silicon Valley',
            role: 'Market Manager Expedia Group',
            testimony:
                'Ha sido un honor y un placer enorme haber coincidido con un grupo de profesionales y personas tan extraodinarios. Las conversaciones y las conexiones creadas superaron con creces cualquier expectativa que tenía. Me llevo valiosos aprendizajes y, sobre todo, la alegría de nuevos lazos.',
            avatarUrl: massielRivera,
        },
    ];

    const goToNext = useCallback(() => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentIndex((prev) => (prev + 1) % testimonies.length);
    }, [isTransitioning, testimonies.length]);

    const goToPrev = useCallback(() => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentIndex(
            (prev) => (prev - 1 + testimonies.length) % testimonies.length
        );
    }, [isTransitioning, testimonies.length]);

    const goToSlide = useCallback(
        (index) => {
            if (isTransitioning || index === currentIndex) return;
            setIsTransitioning(true);
            setCurrentIndex(index);
        },
        [isTransitioning, currentIndex]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsTransitioning(false);
        }, fadeDuration);

        return () => clearTimeout(timer);
    }, [currentIndex, fadeDuration]);

    useEffect(() => {
        if (isPaused || testimonies.length <= 1) return;

        const interval = setInterval(() => {
            goToNext();
        }, autoPlayInterval);

        return () => clearInterval(interval);
    }, [isPaused, autoPlayInterval, goToNext, testimonies.length]);

    const handleMouseEnter = () => {
        if (pauseOnHover) setIsPaused(true);
    };

    const handleMouseLeave = () => {
        if (pauseOnHover) setIsPaused(false);
    };

    const minSwipeDistance = 50;

    const handleTouchStart = (e) => {
        setTouchEnd(0);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrev();
        }
    };

    return (
        <GuestTestimonyCarouselWrapper
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <CarouselContainer
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {testimonies.map((testimony, index) => (
                    <CarouselCardContainer
                        key={index}
                        isActive={index === currentIndex}
                        fadeDuration={fadeDuration}
                    >
                        <GuestTestimonyCard {...testimony} />
                    </CarouselCardContainer>
                ))}
            </CarouselContainer>

            {/* Navigation Dots */}
            {showDots && testimonies.length > 1 && (
                <DotsContainer>
                    {testimonies.map((_, index) => (
                        <DotButton
                            key={index}
                            onClick={() => goToSlide(index)}
                            disabled={isTransitioning}
                            isActive={index === currentIndex}
                            aria-label={`Ir al testimonio ${index + 1}`}
                        />
                    ))}
                </DotsContainer>
            )}
        </GuestTestimonyCarouselWrapper>
    );
};

const GuestTestimonyCarouselWrapper = styled.div`
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    position: relative;
    box-sizing: border-box;
`;

const CarouselContainer = styled.div`
    position: relative;
    overflow: hidden;
    border-radius: 24px;
    background: linear-gradient(to bottom right, #f0f0f0, #fafafa);
    height: 400px;
    touch-action: pan-y; /* Permite scroll vertical pero intercepta horizontal */
    user-select: none; /* Evita selección de texto al deslizar */
`;

const CarouselCardContainer = styled.div`
    position: absolute;
    inset: 0;
    opacity: ${(props) => (props.isActive ? 1 : 0)};
    pointer-events: ${(props) => (props.isActive ? 'auto' : 'none')};
    transition: opacity ${(props) => props.fadeDuration}ms ease-in-out;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const DotsContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
`;

const DotButton = styled.button`
    height: 0.625rem;
    width: ${(props) => (props.isActive ? '2rem' : '0.625rem')};
    border-radius: 9999px;
    background-color: ${(props) => (props.isActive ? '#b31d15' : '#d1d5db')};
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;

    &:hover:not(:disabled) {
        background-color: ${(props) =>
            props.isActive ? '#b31d15' : '#9ca3af'};
        transform: scale(1.1);
    }

    &:disabled {
        cursor: not-allowed;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(179, 29, 21, 0.3);
    }
`;
